import React, { useState } from "react";
import { Button, Badge, Progress } from "flowbite-react";
import { HiOutlineDocumentText } from "react-icons/hi";
import ProjectDashboardDrawer from "../../../pages/ProjectDashboard/ProjectDashboardDrawer";
import { getStatusColor, getProgressColor, formatStatusForDisplay } from "./StatusUtils";
import { 
  getSourceFromObject, 
  getProjectTitle, 
  getProjectEntityName,
  getCompletionPercentage,
  Project
} from "./SourceUtils";

interface ProjectDetailCardProps {
  project: Project;
  openDashboard: (projectId: string) => void;
  currentRoadmapGroup?: any; // Add current roadmap group data
}

// Define a type for the displayed status
type DisplayStatus = "On Track" | "Completed" | "Delayed" | "Blocked" | "Not Started" | "Data Collecting";

const ProjectDetailCard: React.FC<ProjectDetailCardProps> = ({ 
  project, 
  openDashboard, 
  currentRoadmapGroup = null
}) => {
  const [showDashboardDrawer, setShowDashboardDrawer] = useState(false);

  const handleOpenDashboard = () => {
    // Disabled for now
    // setShowDashboardDrawer(true);
    console.log("Dashboard button clicked but functionality is disabled");
  };

  // Get the source directly using the shared utility function
  const source = getSourceFromObject(project);

  // Get stats data from API response if available
  const getStatsData = () => {
    if (currentRoadmapGroup && currentRoadmapGroup.stats) {
      const stats = currentRoadmapGroup.stats;
      return {
        total: stats.total_roadmaps || 0,
        onTrack: stats.on_track_roadmaps || 0,
        completed: stats.completed_roadmaps || 0,
        delayed: stats.delayed_roadmaps || 0,
        blocked: stats.blocked_roadmaps || 0
      };
    }
    
    // Default stats from project object with type assertions
    return {
      total: project.epics.length,
      onTrack: project.epics.filter(e => formatStatusForDisplay(e.status) as DisplayStatus === "On Track").length,
      completed: project.epics.filter(e => formatStatusForDisplay(e.status) as DisplayStatus === "Completed").length,
      delayed: project.epics.filter(e => formatStatusForDisplay(e.status) as DisplayStatus === "Delayed").length,
      blocked: project.epics.filter(e => formatStatusForDisplay(e.status) as DisplayStatus === "Blocked").length
    };
  };

  const stats = getStatsData();

  return (
    <div className="min-w-[400px] max-w-[400px] bg-white dark:bg-slate-900 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col" style={{ maxHeight: "600px" }}>
      <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/80 flex justify-between items-center">
        <h3 className="font-semibold text-slate-900 dark:text-white">
          {getProjectTitle(source)}
        </h3>
      </div>
      <div className="p-4 flex-1 overflow-y-auto">
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Badge
              color="info"
              className={getStatusColor(project.status)}
            >
              {formatStatusForDisplay(project.status)}
            </Badge>
            <span className="text-sm text-slate-500 dark:text-gray-300">
              {project.startDate} - {project.endDate}
            </span>
          </div>
          <h4 className="font-medium text-lg text-slate-900 dark:text-white">
            {getProjectEntityName(source)}: {project.name}
          </h4>

          <div className="bg-slate-50 dark:bg-[#2f3a4c] p-4 rounded-lg">
            <h4 className="font-medium mb-3 text-slate-900 dark:text-white">Project Summary</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-xs text-slate-500 dark:text-gray-300">
                  People Allocated
                </div>
                <div className="text-lg font-semibold text-slate-900 dark:text-white">
                  {currentRoadmapGroup ? currentRoadmapGroup.people_allocated : project.peopleAllocated}
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500 dark:text-gray-300">
                  {source === 'azure' ? 'Total Roadmaps' : 'Total EPICs'}
                </div>
                <div className="text-lg font-semibold text-slate-900 dark:text-white">
                  {stats.total}
                </div>
              </div>
              
              {!currentRoadmapGroup && (
                <>
                  <div>
                    <div className="text-xs text-slate-500 dark:text-gray-300">
                      EPICs with Blockers
                    </div>
                    <div className="text-lg font-semibold text-slate-900 dark:text-white">
                      {project.epics.filter((e) => e.blockers && e.blockers > 0).length}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 dark:text-gray-300">
                      Total Blockers
                    </div>
                    <div className="text-lg font-semibold text-slate-900 dark:text-white">
                      {project.epics.reduce((acc, epic) => acc + (epic.blockers || 0), 0)}
                    </div>
                  </div>
                </>
              )}
              
              <div>
                <div className="text-xs text-slate-500 dark:text-gray-300">
                  {source === 'azure' ? 'On Track Roadmaps' : 'On Track EPICs'}
                </div>
                <div className="text-lg font-semibold text-emerald-600 dark:text-emerald-400">
                  {stats.onTrack}
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500 dark:text-gray-300">
                  {source === 'azure' ? 'Delayed Roadmaps' : 'Delayed EPICs'}
                </div>
                <div className="text-lg font-semibold text-amber-600 dark:text-amber-400">
                  {stats.delayed}
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500 dark:text-gray-300">
                  {source === 'azure' ? 'Completed Roadmaps' : 'Completed EPICs'}
                </div>
                <div className="text-lg font-semibold text-blue-600 dark:text-blue-400">
                  {stats.completed}
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500 dark:text-gray-300">
                  {source === 'azure' ? 'Blocked Roadmaps' : 'Blocked EPICs'}
                </div>
                <div className="text-lg font-semibold text-rose-600 dark:text-rose-400">
                  {stats.blocked}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-slate-50 dark:bg-[#2f3a4c] p-4 rounded-lg">
            <h4 className="font-medium mb-2 text-slate-900 dark:text-white">
              {source === 'azure' ? 'Roadmap Status' : 'EPIC Status'}
            </h4>
            <div className="space-y-3">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-slate-900 dark:text-white">Overall Completion</span>
                  <span className="text-sm font-medium text-slate-900 dark:text-white">
                    {currentRoadmapGroup && typeof currentRoadmapGroup.progress === 'number'
                      ? `${(currentRoadmapGroup.progress).toFixed(0)}`
                      : getCompletionPercentage(
                          project.epics.filter((e) => formatStatusForDisplay(e.status) as DisplayStatus === "Completed").length,
                          project.epics.length,
                        )
                    }
                    %
                  </span>
                </div>
                <div className="h-2 rounded-full bg-slate-200 dark:bg-slate-700">
                  <div 
                    className={`h-full rounded-full ${getProgressColor(
                      currentRoadmapGroup && typeof currentRoadmapGroup.progress === 'number'
                        ? currentRoadmapGroup.progress
                        : getCompletionPercentage(
                            project.epics.filter((e) => formatStatusForDisplay(e.status) as DisplayStatus === "Completed").length,
                            project.epics.length,
                          )
                    )}`}
                    style={{ 
                      width: `${currentRoadmapGroup && typeof currentRoadmapGroup.progress === 'number'
                        ? currentRoadmapGroup.progress
                        : getCompletionPercentage(
                            project.epics.filter((e) => formatStatusForDisplay(e.status) as DisplayStatus === "Completed").length,
                            project.epics.length,
                          )}%` 
                    }}
                  ></div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 mt-3">
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-emerald-500"></div>
                  <span className="text-sm text-slate-700 dark:text-gray-300">On Track</span>
                  <span className="text-sm font-medium ml-auto text-slate-900 dark:text-white">
                    {stats.onTrack}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-amber-500"></div>
                  <span className="text-sm text-slate-700 dark:text-gray-300">Delayed</span>
                  <span className="text-sm font-medium ml-auto text-slate-900 dark:text-white">
                    {stats.delayed}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-blue-500"></div>
                  <span className="text-sm text-slate-700 dark:text-gray-300">Completed</span>
                  <span className="text-sm font-medium ml-auto text-slate-900 dark:text-white">
                    {stats.completed}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-rose-500"></div>
                  <span className="text-sm text-slate-700 dark:text-gray-300">Blocked</span>
                  <span className="text-sm font-medium ml-auto text-slate-900 dark:text-white">
                    {stats.blocked}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Visual distribution bar */}
            {stats.total > 0 && (
              <div className="mt-4">
                <div className="text-xs text-slate-500 dark:text-gray-400 mb-2">Status Distribution</div>
                <div className="flex h-4 rounded-md overflow-hidden">
                  {stats.onTrack > 0 && (
                    <div 
                      className="bg-emerald-500" 
                      style={{ width: `${(stats.onTrack / stats.total) * 100}%` }}
                      title={`On Track: ${stats.onTrack}`}
                    ></div>
                  )}
                  {stats.completed > 0 && (
                    <div 
                      className="bg-blue-500" 
                      style={{ width: `${(stats.completed / stats.total) * 100}%` }}
                      title={`Completed: ${stats.completed}`}
                    ></div>
                  )}
                  {stats.delayed > 0 && (
                    <div 
                      className="bg-amber-500" 
                      style={{ width: `${(stats.delayed / stats.total) * 100}%` }}
                      title={`Delayed: ${stats.delayed}`}
                    ></div>
                  )}
                  {stats.blocked > 0 && (
                    <div 
                      className="bg-rose-500" 
                      style={{ width: `${(stats.blocked / stats.total) * 100}%` }}
                      title={`Blocked: ${stats.blocked}`}
                    ></div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="p-4 border-t border-slate-200 dark:border-slate-700">
        <Button
          className="w-full  opacity-60"
          color="light"
          onClick={handleOpenDashboard}
          title="Coming Soon"
          
        >
          <HiOutlineDocumentText className="h-4 w-4 mr-2" />
          View Detailed Dashboard
        </Button>
      </div>
      
      {/* Dashboard Drawer */}
      <ProjectDashboardDrawer 
        isVisible={showDashboardDrawer}
        setShowModal={setShowDashboardDrawer}
        projectId={project.id}
      />
    </div>
  );
};

export default ProjectDetailCard;